import { useMemo, useState, type ReactNode } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowDownUp,
  Check,
  ChevronDown,
  History,
  Mail,
  MessageCircle,
  MessageSquare,
  NotebookPen,
  Phone,
  Plus,
  Search,
  TimerOff,
  Users,
  X,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, EmptyState, PageHeader, Skeleton, cx } from "../components/ui";
import { QuickAddForm } from "../components/QuickAddForm";
import { LeadDetailModal } from "../components/LeadDetailModal";
import { WinLeadPanel } from "../components/WinLeadPanel";
import { telLink, whatsappLink, whatsappNumber } from "../lib/links";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDateTime, formatElapsed, formatMoney, initials } from "../lib/format";
import type { Activity, ActivityWrite, Lead } from "../api/types";

/** High-value threshold, for the "High value" filter chip. */
const HIGH_VALUE = 25000;

/**
 * The tiles, in reading order.
 *
 * The keys are the buckets the API returns, so a tile added server-side appears
 * here by adding one entry -- and nothing else. The wording is the business's:
 * New is nobody contacted them yet, Open is the work in progress, Won is the
 * ones they converted, Lost is the ones they did not.
 *
 * "Won" and not "Confirmed", deliberately. The green CONFIRMED box on a
 * tentative time means they *agreed the date*; a tile called Confirmed counting
 * converted leads meant clicking it showed nothing while five rows sat in the
 * queue wearing a green CONFIRMED badge. One word, two meanings, and the user
 * who has to work the queue is the one who notices.
 */
const BUCKETS = [
  { key: "new", label: "New", hint: "Not contacted yet", icon: Plus, tone: "sky" },
  { key: "open", label: "Open", hint: "In progress", icon: Users, tone: "emerald" },
  { key: "stale", label: "Stale", hint: "More than 3 days without contact", icon: TimerOff, tone: "rose" },
  { key: "won", label: "Won", hint: "Converted — now customers", icon: Check, tone: "emerald" },
  { key: "lost", label: "Lost", hint: "Not worth pursuing", icon: X, tone: "slate" },
] as const;

type Scope = "open" | "all" | "today" | "high" | "confirmed";
type BucketKey = "new" | "open" | "stale" | "won" | "lost";

/**
 * The queue.
 *
 * Columns answer the three questions a person opening this has: who is it, when
 * did we last reach them, and what is it worth if it converts. The value column
 * sits deliberately close to the tentative time, because the two are read
 * together -- "is this worth chasing, and have they actually agreed to come?"
 */
export function LeadsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const timeZone = useAccountTimeZone();

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<Scope>("open");
  const [bucket, setBucket] = useState<BucketKey | null>(null);
  const [owner, setOwner] = useState("");
  const [sort, setSort] = useState<"tentative" | "value" | "last_contact" | "newest">("tentative");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [composingFor, setComposingFor] = useState<string | null>(null);
  const [winning, setWinning] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ["lead-summary"],
    queryFn: ({ signal }) => api.leadSummary(signal),
    staleTime: 15_000,
  });

  const query = useQuery({
    queryKey: ["leads", scope, bucket, owner, search],
    // Keep the rows on screen while a new filter loads. Without this the table
    // blanks on every tile click, which on a slow connection reads as "no leads
    // in this bucket" rather than "loading".
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api.leads(
        {
          // A bucket is the server's definition of that set, so it replaces the
          // view filter rather than intersecting with it.
          bucket: bucket ?? undefined,
          view: bucket ? "all" : scope === "all" ? "all" : undefined,
          search: search || undefined,
          page_size: 100,
        },
        signal,
      ),
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 10 * 60_000,
  });

  const recent = useQuery({
    queryKey: ["lead-activities", query.data?.results.map((l) => l.id)],
    queryFn: ({ signal }) =>
      api.leadRecentActivities((query.data?.results ?? []).map((l) => l.id), 3, signal),
    enabled: (query.data?.results.length ?? 0) > 0,
  });

  const recentByLead = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const row of recent.data ?? []) map.set(row.lead_id, row.activities);
    return map;
  }, [recent.data]);

  const lastContactByLead = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of recent.data ?? []) map.set(row.lead_id, row.last_contact_at ?? null);
    return map;
  }, [recent.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["leads"] });
    void queryClient.invalidateQueries({ queryKey: ["lead-summary"] });
    void queryClient.invalidateQueries({ queryKey: ["lead-activities"] });
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
  };

  const setStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => api.setLeadStatus(id, next),
    onSuccess: invalidate,
  });
  const confirm = useMutation({
    mutationFn: (id: string) => api.confirmLead(id),
    onSuccess: invalidate,
  });
  const convert = useMutation({
    mutationFn: (id: string) => api.convertLead(id),
    onSuccess: invalidate,
  });
  const bulk = useMutation({
    mutationFn: ({ ids, next }: { ids: string[]; next: string }) =>
      api.bulkLeadStatus(ids, next),
    onSuccess: invalidate,
  });

  const all = query.data?.results ?? [];

  // Scope, owner and sort are applied here rather than as query params: the queue
  // is bounded and already fetched, and round-tripping "show me only today's" to
  // a 140ms-away server for a list we hold would be absurd.
  const rows = useMemo(() => {
    const today = new Date().toDateString();
    let out = all;

    if (scope === "open") out = out.filter((l) => l.is_open);
    if (scope === "today") {
      out = out.filter(
        (l) => l.requested_start_at && new Date(l.requested_start_at).toDateString() === today,
      );
    }
    if (scope === "high") {
      out = out.filter((l) => Number(l.quoted_price?.amount ?? 0) >= HIGH_VALUE);
    }
    // A time they have agreed is not a sale, so it is a lens and not a tile.
    if (scope === "confirmed") {
      out = out.filter((l) => Boolean(l.confirmed_at));
    }
    if (owner) out = out.filter((l) => l.assigned_employee === owner);

    const byDate = (v?: string | null) => (v ? new Date(v).getTime() : 0);
    const value = (l: Lead) => Number(l.quoted_price?.amount ?? 0);

    return [...out].sort((a, b) => {
      switch (sort) {
        case "value":
          return value(b) - value(a);
        case "last_contact":
          return byDate(lastContactByLead.get(b.id)) - byDate(lastContactByLead.get(a.id));
        case "newest":
          return byDate(b.created_at) - byDate(a.created_at);
        default:
          // Soonest tentative first, and leads with no time at the end rather
          // than treated as infinitely urgent.
          return (byDate(a.requested_start_at) || Infinity) - (byDate(b.requested_start_at) || Infinity);
      }
    });
  }, [all, scope, owner, sort, lastContactByLead]);

  const canWrite = (user?.permissions ?? []).includes("contacts.write");
  const s = summary.data;
  const currency = rows.find((l) => l.quoted_price)?.quoted_price?.currency ?? "INR";
  const selectedIds = [...selected];

  // Buckets come from the API, so adding a tile is a backend change. `stale`
  // reads its threshold from the response rather than repeating 3 here.
  const buckets = useMemo(
    () => Object.fromEntries((s?.buckets ?? []).map((b) => [b.key, b])),
    [s],
  );
  const staleHint = s
    ? `More than ${s.stale_after_days} days without contact`
    : "More than 3 days without contact";

  const activeBucket: BucketKey | null = bucket;

  /** A tile filters the list. Clicking it again clears back to the default view. */
  function applyBucket(key: BucketKey) {
    const next = activeBucket === key ? null : key;
    setBucket(next);
    setScope(next ? "all" : "open");
    setSelected(new Set());
  }

  function setScopeClearingBucket(next: Scope) {
    setScope(next);
    setBucket(null);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle={
          s
            ? `${buckets.open?.count ?? 0} open · ${buckets.stale?.count ?? 0} stale · ${
                s.upcoming
              } with a tentative time`
            : "What people are asking about"
        }
        actions={
          canWrite ? (
            <Link
              to="/customers/leads/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus size={16} /> Capture lead
            </Link>
          ) : null
        }
      />

      {/* The queue's shape. Each tile is a filter, so the numbers double as
          navigation rather than sitting there being looked at. Order starts at
          New because that is where somebody picking this up starts. */}
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {BUCKETS.map((bucket) => (
          <QueueTile
            key={bucket.key}
            label={bucket.label}
            hint={bucket.key === "stale" ? staleHint : bucket.hint}
            icon={<bucket.icon size={17} />}
            tone={bucket.tone}
            count={buckets[bucket.key]?.count ?? null}
            value={buckets[bucket.key]?.value ?? null}
            currency={currency}
            active={activeBucket === bucket.key}
            onClick={() => applyBucket(bucket.key)}
          />
        ))}
      </div>

      {bucket ? (
        <p className="mb-3 flex items-center gap-2 text-sm text-slate-600">
          Showing
          <span className="font-medium text-slate-900">
            {BUCKETS.find((b) => b.key === bucket)?.label}
          </span>
          <Button
            onClick={() => setScopeClearingBucket("open")}
            className="px-2 py-0.5 text-xs"
          >
            Clear
          </Button>
        </p>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, phone or notes"
            aria-label="Search leads"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        {(
          [
            ["all", "All"],
            ["open", "Open"],
            ["today", "Tentative today"],
            ["high", "High value"],
            ["confirmed", "Time confirmed"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setScopeClearingBucket(value)}
            className={cx(
              "rounded-full px-3 py-1.5 text-xs font-medium transition",
              scope === value
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}

        <Select
          value={owner}
          onChange={setOwner}
          ariaLabel="Owner"
          className="w-auto"
        >
          <option value="">All owners</option>
          {(employees.data ?? []).map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.display_name || employee.employee_code}
            </option>
          ))}
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <ArrowDownUp size={14} className="text-slate-400" />
          <Select value={sort} onChange={(v) => setSort(v as typeof sort)} ariaLabel="Sort by" className="w-auto">
            <option value="tentative">Tentative time</option>
            <option value="value">Lead value</option>
            <option value="last_contact">Last contact</option>
            <option value="newest">Newest</option>
          </Select>
        </div>
      </div>

      {selectedIds.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-white">
          <span className="text-sm">
            {selectedIds.length} selected
          </span>
          <Button
            className="bg-white/15 text-white ring-0 hover:bg-white/25"
            onClick={() => bulk.mutate({ ids: selectedIds, next: "contacted" })}
          >
            Mark contacted
          </Button>
          <Button
            className="bg-white/15 text-white ring-0 hover:bg-white/25"
            onClick={() => bulk.mutate({ ids: selectedIds, next: "unqualified" })}
          >
            Mark lost
          </Button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto rounded p-1 text-white/70 hover:bg-white/15"
            aria-label="Clear selection"
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState
            title="Could not load leads"
            body={query.error instanceof ApiError ? query.error.message : "Please try again."}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={28} />}
            title={search || owner ? "Nothing matches that" : "No leads here"}
            body={
              search || owner
                ? "Try clearing the filters."
                : "When somebody rings to ask about a price, capture it here."
            }
          />
        ) : (
          <LeadTable
            rows={rows}
            currency={currency}
            timeZone={timeZone}
            canWrite={canWrite}
            selected={selected}
            lastContactByLead={lastContactByLead}
            recentByLead={recentByLead}
            busy={setStatus.isPending || confirm.isPending || convert.isPending || bulk.isPending}
            onToggle={toggle}
            onToggleAll={() =>
              setSelected((prev) =>
                prev.size === rows.length ? new Set() : new Set(rows.map((l) => l.id)),
              )
            }
            onOpen={setOpenLeadId}
            onSetStatus={(id, next) => setStatus.mutate({ id, next })}
            onLose={(id) => setStatus.mutate({ id, next: "unqualified" })}
            onCompose={(id) => setComposingFor(composingFor === id ? null : id)}
            composingFor={composingFor}
            winning={winning}
            onWin={(id) => setWinning(winning === id ? null : id)}
            onLogActivity={(id, body) =>
              api.logLeadActivity(id, body).then((saved) => {
                invalidate();
                setComposingFor(null);
                return saved;
              })
            }
          />
        )}
      </div>

      <LeadDetailModal
        leadId={openLeadId}
        onClose={() => setOpenLeadId(null)}
        onChanged={invalidate}
      />
    </>
  );
}

function LeadTable({
  rows,
  currency,
  timeZone,
  canWrite,
  selected,
  lastContactByLead,
  recentByLead,
  busy,
  onToggle,
  onToggleAll,
  onOpen,
  onSetStatus,
  onLose,
  onCompose,
  composingFor,
  winning,
  onWin,
  onLogActivity,
}: {
  rows: Lead[];
  currency: string;
  timeZone: string | undefined;
  canWrite: boolean;
  selected: Set<string>;
  lastContactByLead: Map<string, string | null>;
  recentByLead: Map<string, Activity[]>;
  busy: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onOpen: (id: string) => void;
  onSetStatus: (id: string, next: string) => void;
  onLose: (id: string) => void;
  onCompose: (id: string) => void;
  composingFor: string | null;
  winning: string | null;
  /** Opens the win panel for a row, or closes it if that row is already open. */
  onWin: (id: string) => void;
  onLogActivity: (id: string, body: ActivityWrite) => Promise<unknown>;
}) {
  const allSelected = rows.length > 0 && rows.every((l) => selected.has(l.id));

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
          <th className="w-10 px-3 py-2.5">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              aria-label="Select all"
              className="size-3.5 rounded border-slate-300"
            />
          </th>
          <th className="px-3 py-2.5">Lead</th>
          <th className="px-3 py-2.5">Last contact &amp; status</th>
          <th className="px-3 py-2.5">Tentative time</th>
          <th className="px-3 py-2.5 text-right">Lead value</th>
          <th className="px-3 py-2.5" />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((lead) => {
          const lastContact = lastContactByLead.get(lead.id) ?? null;
          const phone = lead.phone || lead.contact_phone || "";
          const activities = recentByLead.get(lead.id) ?? [];
          const latestNote = activities.find((a) => a.type === "note")?.subject;
          const latestCall = activities[0];

          return (
            <tr
              key={lead.id}
              className={cx("align-top hover:bg-slate-50", lead.is_stale && "bg-rose-50/40")}
            >
              <td className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={selected.has(lead.id)}
                  onChange={() => onToggle(lead.id)}
                  aria-label={`Select ${lead.display_name}`}
                  className="mt-1 size-3.5 rounded border-slate-300"
                />
              </td>

              {/* Name opens the detail modal. */}
              <td className="px-3 py-3">
                <div className="flex items-start gap-2.5">
                  <span
                    className={cx(
                      "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                      avatarTone(lead.display_name),
                    )}
                  >
                    {initials(lead.display_name || "?")}
                  </span>
                  <div className="min-w-0">
                    <button
                      onClick={() => onOpen(lead.id)}
                      className="flex items-center gap-1.5 text-left"
                    >
                      <span className="truncate text-sm font-medium text-slate-900 hover:underline">
                        {lead.display_name || "Unnamed enquiry"}
                      </span>
                      <StageChip status={lead.status} />
                    </button>
                    <p className="mt-0.5 flex items-center gap-2 truncate text-xs text-slate-500">
                      {phone && telLink(phone) ? (
                        <>
                          <a
                            href={telLink(phone) ?? undefined}
                            title={`Call ${phone}`}
                            aria-label={`Call ${lead.display_name}`}
                            className="inline-flex items-center gap-1 hover:text-slate-900 hover:underline"
                          >
                            <Phone size={12} className="shrink-0" />
                            {phone}
                          </a>
                          {whatsappNumber(phone) ? (
                            <a
                              href={whatsappLink(phone) ?? undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Message on WhatsApp"
                              aria-label={`Message ${lead.display_name} on WhatsApp`}
                              className="shrink-0 rounded p-0.5 text-emerald-700 hover:bg-emerald-50"
                            >
                              <MessageCircle size={13} />
                            </a>
                          ) : null}
                        </>
                      ) : (lead.email || lead.contact_email) ? (
                        <>
                          <Mail size={12} className="shrink-0" />
                          {lead.email || lead.contact_email}
                        </>
                      ) : (
                        "—"
                      )}
                    </p>
                    {lead.notes ? (
                      <p className="mt-0.5 line-clamp-1 max-w-[16rem] text-xs text-slate-400">
                        {lead.notes}
                      </p>
                    ) : null}
                  </div>
                </div>
              </td>

              <td className="px-3 py-3">
                <p className="text-xs tabular-nums text-slate-600">
                  {lastContact ? formatDateTime(lastContact, timeZone) : "—"}
                </p>
                <StaleBadge lead={lead} lastContact={lastContact} />

                {canWrite ? (
                  <StatusSelect
                    lead={lead}
                    busy={busy}
                    onChange={(next) => onSetStatus(lead.id, next)}
                  />
                ) : (
                  <StageChip status={lead.status} />
                )}

                {latestCall ? (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <ActivityGlyph type={latestCall.type} />
                    <span className="text-[13px] capitalize text-slate-600">{latestCall.type}</span>
                    {formatElapsed(latestCall.created_at)
                      ? ` — ${formatElapsed(latestCall.created_at)}`
                      : ""}
                  </p>
                ) : null}

                {latestNote ? (
                  <p className="mt-0.5 line-clamp-1 max-w-[18rem] text-xs text-slate-400">
                    {latestNote}
                  </p>
                ) : null}
              </td>

              <td className="px-3 py-3">
                {lead.requested_start_at ? (
                  <div
                    className={cx(
                      "inline-block rounded-md px-2.5 py-1.5 ring-1 ring-inset",
                      lead.is_confirmed
                        ? "bg-emerald-50 ring-emerald-600/25"
                        : "bg-amber-50 ring-amber-600/20",
                    )}
                  >
                    <p
                      className={cx(
                        "text-[10px] font-medium uppercase tracking-wide",
                        lead.is_confirmed ? "text-emerald-700" : "text-amber-700",
                      )}
                    >
                      {lead.is_confirmed ? "Confirmed" : "Tentative"}
                    </p>
                    <p
                      className={cx(
                        "text-xs font-medium tabular-nums",
                        lead.is_confirmed ? "text-emerald-900" : "text-amber-900",
                      )}
                    >
                      {formatDateTime(lead.requested_start_at, timeZone)}
                    </p>
                  </div>
                ) : (
                  <span className="text-xs text-slate-300">—</span>
                )}
              </td>

              <td className="px-3 py-3 text-right">
                <p className="text-base font-semibold tabular-nums text-emerald-700">
                  {lead.quoted_price
                    ? formatMoney({ ...lead.quoted_price, currency })
                    : "—"}
                </p>
                {lead.product_name ? (
                  <p className="mt-0.5 max-w-[10rem] truncate text-xs text-slate-500">
                    {lead.product_name}
                  </p>
                ) : null}
              </td>

              <td className="px-3 py-3">
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  {canWrite ? (
                    <>
                      {lead.status !== "converted" ? (
                        <button
                          onClick={() => onWin(lead.id)}
                          aria-expanded={winning === lead.id}
                          disabled={busy}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Check size={12} /> Won
                        </button>
                      ) : null}
                      {lead.status !== "unqualified" ? (
                        <button
                          onClick={() => onLose(lead.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                          <X size={12} /> Lost
                        </button>
                      ) : null}
                      <button
                        onClick={() => onCompose(lead.id)}
                        aria-expanded={composingFor === lead.id}
                        title="Log activity"
                        className="rounded-full bg-white p-1.5 text-slate-500 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                      >
                        <NotebookPen size={13} />
                      </button>
                    </>
                  ) : null}
                </div>

                {winning === lead.id ? (
                  <div className="mt-2 w-full">
                    <WinLeadPanel lead={lead} onClose={() => onWin(lead.id)} />
                  </div>
                ) : null}

                {composingFor === lead.id ? (
                  <div className="mt-2 w-[22rem] max-w-full text-left">
                    <div className="rounded-lg bg-slate-50 p-3">
                      <QuickAddForm
                        onSave={(body) => onLogActivity(lead.id, body)}
                        onCancel={() => onCompose(lead.id)}
                      />
                    </div>
                  </div>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const STATUS_TONE: Record<string, string> = {
  new: "bg-sky-50 text-sky-700 ring-sky-600/20",
  contacted: "bg-amber-50 text-amber-700 ring-amber-600/20",
  qualified: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  unqualified: "bg-slate-100 text-slate-500 ring-slate-400/20",
  converted: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

/**
 * The red stale warning on a queue row.
 *
 * `is_stale` comes from the server, so the badge, the tile count and the
 * `?stale=true` filter all answer the same question. The elapsed label is a
 * duration (`4d`), which is the same fact in every account timezone.
 */
export function StaleBadge({
  lead,
  lastContact,
}: {
  lead: Lead;
  lastContact?: string | null;
}) {
  if (!lead.is_stale) return null;

  const elapsed = formatElapsed(lastContact ?? lead.created_at);
  return (
    <span
      role="status"
      aria-label={`${lead.display_name || "Lead"} is stale`}
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20"
    >
      <TimerOff size={11} aria-hidden="true" />
      Stale{elapsed ? ` · no contact ${elapsed}` : ""}
    </span>
  );
}

function StageChip({ status }: { status?: string | null }) {
  if (!status) return null;
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase ring-1 ring-inset",
        STATUS_TONE[status] ?? STATUS_TONE.unqualified,
      )}
    >
      {status}
    </span>
  );
}

/** The row's status control: only the moves the lifecycle allows. */
function StatusSelect({
  lead,
  busy,
  onChange,
}: {
  lead: Lead;
  busy: boolean;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = lead.next_statuses ?? [];

  if (options.length === 0) return <StageChip status={lead.status} />;

  return (
    <div className="relative mt-1 inline-block">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        disabled={busy}
        className={cx(
          "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase ring-1 ring-inset",
          STATUS_TONE[lead.status ?? ""] ?? STATUS_TONE.unqualified,
        )}
      >
        {lead.status}
        <ChevronDown size={10} />
      </button>
      {open ? (
        <>
          <button
            className="fixed inset-0 z-10 cursor-default"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-200">
            {options.map((next) => (
              <button
                key={next}
                onClick={() => {
                  onChange(next);
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs capitalize text-slate-700 hover:bg-slate-50"
              >
                {next}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function ActivityGlyph({ type }: { type?: string }) {
  const Icon =
    type === "call" ? Phone : type === "email" ? Mail : type === "task" ? Check : History;
  return <Icon size={14} className="shrink-0 text-slate-400" />;
}

/** Stable colour per person, so the same lead keeps the same avatar. */
function avatarTone(name: string): string {
  const tones = [
    "bg-pink-100 text-pink-700",
    "bg-sky-100 text-sky-700",
    "bg-emerald-100 text-emerald-700",
    "bg-violet-100 text-violet-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
  ];
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 9973;
  return tones[hash % tones.length]!;
}

function QueueTile({
  label,
  count,
  value,
  currency,
  hint,
  icon,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number | null | undefined;
  /** Potential value of this group. Every tile carries one. */
  value: string | number | null | undefined;
  currency: string;
  hint: string;
  icon: ReactNode;
  tone: "sky" | "rose" | "emerald" | "slate";
  active: boolean;
  onClick: () => void;
}) {
  const ring = {
    sky: "bg-sky-50 text-sky-600",
    rose: "bg-rose-50 text-rose-600",
    emerald: "bg-emerald-50 text-emerald-600",
    slate: "bg-slate-100 text-slate-500",
  }[tone];

  const worth = value === null || value === undefined || Number(value) === 0
    ? null
    : formatMoney({ amount: String(value), currency });

  return (
    <button onClick={onClick} className="text-left">
      <Card
        className={cx(
          "h-full transition",
          active && "ring-2 ring-slate-900",
          count === 0 && "opacity-70",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          <span className={cx("shrink-0 rounded-full p-2", ring)}>{icon}</span>
        </div>
        <p className="mt-2 text-4xl font-bold leading-none tracking-tight text-slate-900">
          {count ?? "—"}
        </p>
        <p className="mt-2 truncate text-xs text-slate-500">{hint}</p>
        <p className="mt-0.5 truncate text-xs font-medium text-emerald-700">
          {worth ? `Potential value ${worth}` : "Potential value —"}
        </p>
      </Card>
    </button>
  );
}

function Select({
  value,
  onChange,
  ariaLabel,
  className,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
      className={cx(
        "rounded-full border-0 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900",
        className,
      )}
    >
      {children}
    </select>
  );
}
